import type { RefObject } from 'react';
import { useEffect, useEffectEvent } from 'react';

import { shouldIgnoreOutsideInteraction } from '@/utils/third-party-editor-ui';

/**
 * Outside-click dismissal for `isNonModal` popovers.
 *
 * Why this exists — the react-aria built-ins all fail inside our slideouts:
 *
 * 1. MODAL popovers (the react-aria default) dismiss via `useOverlay`'s
 *    interact-outside + a full-viewport underlay, and apply `inert` to
 *    everything outside the popover (`ariaHideOutside(…, {shouldUseInert})`).
 *    The underlay renders with `z-index: auto`, so inside any surface that
 *    sets an explicit stacking index (a slideout at z≈810, the popover itself
 *    at z≈900 via `useStackingZIndex`) it paints beneath the page.
 * 2. NON-MODAL popovers skip the underlay/inert, but `usePopover` passes
 *    `isDismissable: !isNonModal` to `useOverlay` — outside-POINTER dismissal
 *    is disabled entirely. Only `shouldCloseOnBlur` remains, and `useOverlay`
 *    deliberately ignores a blur whose `relatedTarget` is null (focus lost to
 *    `<body>`, react-spectrum #4130) — exactly what a click on empty,
 *    non-focusable page area produces.
 * 3. react-aria's `useInteractOutside` ignores ANY event whose target sits
 *    inside a `[data-react-aria-top-layer]` subtree. The non-modal slideout
 *    now carries that attribute only through its DOM-insertion window (see
 *    slideout-menu-nonmodal.tsx), but any container that keeps it (hover
 *    cards, editor toolbars, toast regions) still makes clicks inside it
 *    invisible to react-aria's interact-outside handlers. This hook does not
 *    depend on that machinery, so it is unaffected.
 *
 * The same blur rule (2) is why the lib/base `Select` needs this hook too:
 * its focus lives in the listbox, so a click on non-focusable page content
 * produces a relatedTarget-less blur that react-aria ignores.
 *
 * Hence this hook listens at the document itself (capture phase, mirroring
 * react-aria's pointerdown→click pairing so a drag that starts inside the
 * popover never dismisses it) and applies its own validity rules:
 *
 * - Interactions inside the popover element never dismiss.
 * - Interactions inside an overlay the popover OWNS never dismiss either: one
 *   opened from a trigger inside it (a subproperty Select's listbox, a date
 *   picker's calendar, a submenu), found transitively through `aria-controls`
 *   (and a DatePicker dialog's `aria-labelledby`). Those overlays are portaled
 *   to <body> as sibling layers, so the DOM path never crosses the popover —
 *   without this, choosing a nested option closed the whole host popover.
 * - A press on the popover's OWN trigger (an element whose `aria-controls`
 *   targets a node inside this popover) dismisses AND swallows the press
 *   (preventDefault + stopPropagation at document capture — the same move
 *   react-aria's modal `onInteractOutsideStart` makes). Necessary because
 *   `useMenuTrigger`'s press handler only ever calls `state.open()` — react-
 *   aria relies on the modal underlay to close on trigger re-click, and
 *   non-modal has no underlay, so without the swallow the press would
 *   immediately reopen what we just closed.
 * - Exception: when the own-trigger element is already the focused, editable
 *   element (a ComboBox's input, where the trigger and the text field are the
 *   same node), a press on it is the user placing the caret or selecting text
 *   — not a re-open/toggle press — so it does NOT dismiss. Toggle-close-on-
 *   re-click only makes sense for button-style triggers (Select, Dropdown)
 *   that are a separate node from their popover.
 * - Any other outside interaction dismisses WITHOUT being swallowed, so the
 *   click still lands (click-through): pressing the Status trigger closes an
 *   open Due-date calendar and opens the Status menu in one click.
 * - Unlike react-aria, top-layer content does NOT get a free pass: clicking a
 *   toast / hover-popover / floating toolbar while a picker is open dismisses
 *   the picker, which is the standard lightweight-picker behavior.
 * - A press on UI injected by a writing extension (Grammarly's shadow-host
 *   overlay), or on a node already detached from the document, is ignored
 *   entirely (`shouldIgnoreOutsideInteraction`) — it is not our DOM, so it must
 *   not count as an outside press that closes an open editor menu.
 *
 * Regression tests: __tests__/outside-click-dismissal.test.tsx.
 */
export function useDismissOnInteractOutside(
  ref: RefObject<Element | null>,
  isOpen: boolean,
  onDismiss: () => void
): void {
  // Effect Event so the document listeners always call the latest onDismiss
  // without it becoming an effect dependency — re-subscribing on every render
  // would drop the pointerdown/click pairing state mid-gesture
  // (REACT_19_PATTERNS.md § 4.1).
  const dismiss = useEffectEvent(() => {
    onDismiss();
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let pointerDownWasOutside = false;

    // Does this element's `aria-controls` point at a node inside the popover?
    // That identifies the popover's OWN trigger (react-aria stamps
    // aria-controls with the id of the controlled menu/dialog while open).
    const controlsThisPopover = (el: Element, popover: Element): boolean => {
      const controls = el.getAttribute('aria-controls');
      if (!controls) {
        return false;
      }
      return controls.split(/\s+/).some(id => {
        const controlled = document.getElementById(id);
        return (
          controlled != null &&
          (controlled === popover || popover.contains(controlled))
        );
      });
    };

    const isOutside = (e: Event): boolean => {
      const el = ref.current;
      if (!el) {
        return false;
      }
      return !e.composedPath().includes(el);
    };

    // The portal layer a node lives in: its ancestor that is a direct child
    // of <body>. react-aria portals every overlay there, so a Select listbox
    // opened from inside this popover is a SIBLING layer, not a descendant.
    const portalLayerOf = (node: Node | null): Element | null => {
      let current: Element | null =
        node instanceof Element ? node : (node?.parentElement ?? null);
      while (current && current.parentElement !== document.body) {
        current = current.parentElement;
      }
      return current;
    };

    // Is the target inside an overlay this popover OWNS — one opened from a
    // trigger inside it (a subproperty Select, a date picker, a submenu), or
    // transitively from inside such an overlay? A press there is not an
    // outside press, even though the DOM path never crosses the popover.
    // Ownership follows `aria-controls`, which react-aria stamps on every
    // overlay trigger while its overlay is open. Anything the popover does
    // not control — page content, a slideout, a toast — stays outside.
    const isInsideOwnedOverlay = (target: EventTarget, popover: Element) => {
      const targetLayer = portalLayerOf(target as Node);
      const popoverLayer = portalLayerOf(popover);
      if (!targetLayer || targetLayer === popoverLayer) {
        return false;
      }
      const owned: Element[] = [popover];
      for (let i = 0; i < owned.length; i++) {
        for (const controller of owned[i].querySelectorAll('[aria-controls]')) {
          const ids = (controller.getAttribute('aria-controls') ?? '').split(
            /\s+/
          );
          for (const id of ids) {
            const layer = portalLayerOf(
              id ? document.getElementById(id) : null
            );
            if (!layer || layer === popoverLayer || owned.includes(layer)) {
              continue;
            }
            if (layer === targetLayer) {
              return true;
            }
            owned.push(layer);
          }
        }
      }
      // A react-aria DatePicker's trigger does not stamp aria-controls; its
      // calendar dialog is instead labelled BY the trigger. Accept that
      // reverse link into anything already owned.
      const labelledNodes = [
        targetLayer,
        ...targetLayer.querySelectorAll('[aria-labelledby]'),
      ];
      for (const labelled of labelledNodes) {
        const ids = (labelled.getAttribute('aria-labelledby') ?? '').split(
          /\s+/
        );
        for (const id of ids) {
          const label = id ? document.getElementById(id) : null;
          if (label && owned.some(node => node.contains(label))) {
            return true;
          }
        }
      }
      return false;
    };

    const onPointerDown = (e: PointerEvent) => {
      pointerDownWasOutside = false;
      if (e.button > 0) {
        return;
      }
      // UI injected by a writing extension (Grammarly's shadow-host overlay), or
      // a node already detached from the document, is never a real outside
      // press — treating it as one dismisses the popover mid-edit. This is the
      // same guard the hand-rolled `mousedown` listeners use elsewhere; the
      // image-size menu relied on it before it moved onto this shared hook.
      if (shouldIgnoreOutsideInteraction(e.target)) {
        return;
      }
      const el = ref.current;
      if (!el || !isOutside(e)) {
        return;
      }
      const ownTrigger = e
        .composedPath()
        .find(
          node => node instanceof Element && controlsThisPopover(node, el)
        ) as Element | undefined;
      if (ownTrigger) {
        // A press on an already-focused, editable own-trigger (a ComboBox's
        // input) is text-selection/caret-placement, not a toggle press —
        // let it proceed without dismissing (see the doc-comment exception).
        const isEditingOwnTrigger =
          ownTrigger === document.activeElement &&
          (ownTrigger instanceof HTMLInputElement ||
            ownTrigger instanceof HTMLTextAreaElement);
        if (isEditingOwnTrigger) {
          return;
        }
        // Close now and swallow the press so the trigger's open-on-press
        // handler never runs (see the own-trigger note in the doc comment).
        e.preventDefault();
        e.stopPropagation();
        dismiss();
        return;
      }
      // A press inside an overlay opened FROM this popover (its Select's
      // listbox, a nested menu) is not an outside press.
      const target = e.composedPath()[0] ?? e.target;
      if (target && isInsideOwnedOverlay(target, el)) {
        return;
      }
      pointerDownWasOutside = true;
    };
    const onClick = (e: MouseEvent) => {
      if (pointerDownWasOutside && isOutside(e)) {
        dismiss();
      }
      pointerDownWasOutside = false;
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('click', onClick, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('click', onClick, true);
    };
    // `dismiss` is an Effect Event: always sees the latest onDismiss without
    // being a reactive dependency (REACT_19_PATTERNS.md § 4.1).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ref]);
}
